import {
  CheckSquare,
  CornerUpLeft,
  Eye,
  EyeOff,
  IndentDecrease,
  IndentIncrease,
  Lock,
  Trash2,
} from 'lucide-react';
import { Suspense, lazy, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useOutletContext, useParams } from 'react-router-dom';

import type { CodeMirrorHandle } from '@/components/ui/codemirror';
import { Button } from '@/components/ui/button';
import { InlineTitle } from '@/components/ui/inline-title';
import { LoadingState } from '@/components/ui/states';
import { useKbMutation, useKbNote, useKbNotes } from '@/hooks/useKb';
import { kb } from '@/lib/api/endpoints';
import { sourceOffsetFromClick } from '@/lib/click-to-source';
import { renderMarkdown, toggleTaskItem } from '@/lib/markdown';
import { useT } from '@/lib/i18n';
import { cn } from '@/lib/utils';
import type { ShellContext } from '@/components/layout/AppShell';
import { Page } from '@/routes/Today';

const MarkdownCodeEditor = lazy(() =>
  import('@/components/ui/codemirror').then((module) => ({
    default: module.MarkdownCodeEditor,
  })),
);

/**
 * One note: rendered Markdown with live [[wikilinks]], backlinks underneath,
 * click-to-edit into the CodeMirror editor.
 */
export function KbNoteRoute() {
  const t = useT();
  const navigate = useNavigate();
  const { openTask } = useOutletContext<ShellContext>();
  const { noteId = '' } = useParams();
  const [revealed, setRevealed] = useState(false);
  const [draft, setDraft] = useState<string | null>(null);
  const [cursor, setCursor] = useState<number | undefined>(undefined);
  const editor = useRef<CodeMirrorHandle | null>(null);

  const { data: note, isPending } = useKbNote(noteId, revealed);
  const { data: siblings } = useKbNotes(note?.topic_id ?? '');

  const save = useKbMutation(
    (body: string) => kb.updateNote(noteId, { body }),
    () => setDraft(null),
  );
  // The title is a field like any other; it was the one nothing on this
  // screen let you change.
  const rename = useKbMutation((title: string) => kb.updateNote(noteId, { title }));
  const uploadFiles = useKbMutation(async (files: File[]) => {
    // Sequential, so the markdown lands in the order the files were pasted —
    // the same chronology promise the task editor makes.
    for (const file of files) {
      const stored = await kb.uploadNoteAttachment(noteId, file);
      const alt = stored.original_filename.replace(/[[\]]/g, '');
      const snippet = stored.mime_type.startsWith('image/')
        ? `![${alt}](${stored.content_path})`
        : `[${alt}](${stored.content_path})`;
      editor.current?.insert(`\n${snippet}\n`);
    }
    return null;
  });
  const createFromPhantom = useKbMutation(kb.createNote, (created) =>
    navigate(`/kb/notes/${created.id}`),
  );
  const remove = useKbMutation(kb.deleteNote, () => {
    if (note) navigate(`/kb/${note.topic_id}`);
  });

  /** lowercased title → id, for the renderer's wikilink rule. */
  const linkMap = useMemo(() => {
    const map = new Map<string, string | null>();
    for (const link of note?.links ?? []) {
      map.set(link.target_title.toLowerCase(), link.target_note_id);
    }
    return map;
  }, [note?.links]);

  const html = useMemo(() => {
    if (!note) return '';
    const masked = note.is_sensitive && !revealed;
    // Interactive unless masked: a checklist in a note is a checklist, and a
    // box that renders but does nothing is worse than no box.
    return renderMarkdown(masked ? t('kb.maskedBody') : note.body, {
      interactive: !masked,
      kbLinks: linkMap,
    });
  }, [note, revealed, linkMap, t]);

  if (isPending || !note) {
    return (
      <Page>
        <LoadingState />
      </Page>
    );
  }

  return (
    <Page>
      <div className="mb-4 flex items-center gap-2">
        <span className="flex-1" />
        {note.is_sensitive ? (
          <Button variant="secondary" size="sm" onClick={() => setRevealed((r) => !r)}>
            {revealed ? (
              <EyeOff className="h-3.5 w-3.5" aria-hidden />
            ) : (
              <Eye className="h-3.5 w-3.5" aria-hidden />
            )}
            {t(revealed ? 'kb.hide' : 'kb.reveal')}
          </Button>
        ) : null}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            if (window.confirm(t('kb.deleteNoteConfirm'))) remove.mutate(noteId);
          }}
          className="text-content-subtle hover:text-danger"
        >
          <Trash2 className="h-3.5 w-3.5" aria-hidden />
        </Button>
      </div>

      <h1 className="mb-1 flex items-center gap-2 text-2xl font-bold tracking-tight">
        {note.is_sensitive ? (
          <Lock className="h-5 w-5 shrink-0 text-amber-600" aria-hidden />
        ) : null}
        <InlineTitle
          value={note.title}
          onSave={(title) => rename.mutate(title)}
          ariaLabel={t('kb.renameNote')}
          inputClassName="text-2xl"
        />
      </h1>
      <p className="mb-5 text-xs text-content-subtle">
        {t('kb.lastEdit', { who: note.last_edited_by.display_name })}
      </p>

      {draft !== null ? (
        <div className="overflow-hidden rounded-xl ring-1 ring-accent">
          <Suspense fallback={<LoadingState />}>
            <MarkdownCodeEditor
              initialCursor={cursor}
              value={draft}
              onChange={setDraft}
              onReady={(handle) => {
                editor.current = handle;
              }}
              onPasteFiles={(files) => uploadFiles.mutate(files)}
              onSubmit={(text) => save.mutate(text)}
              onCancel={() => setDraft(null)}
              placeholder={t('kb.bodyPlaceholder')}
              extraCompletions={(siblings ?? []).map((sibling) => sibling.title)}
              className="max-h-[34rem] min-h-[14rem] overflow-y-auto bg-surface"
            />
          </Suspense>
          <div className="flex flex-wrap items-center gap-2 border-t border-border bg-surface-sunken px-2 py-1.5 text-[11px] text-content-subtle">
            {/* A word, not a glyph: making a to-do is the one thing people open
                this editor for, and as a 14px icon among others nobody found it. */}
            <button
              type="button"
              onClick={() => editor.current?.toggleTask()}
              title={t('editor.todo')}
              className="flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium text-content ring-1 ring-border transition-surface hover:bg-surface-hover max-sm:min-h-11 max-sm:px-3 touch:min-h-11 touch:px-3"
            >
              <CheckSquare className="h-4 w-4" aria-hidden />
              {t('editor.todoShort')}
            </button>
            <button
              type="button"
              onClick={() => editor.current?.indent()}
              title={t('editor.indent')}
              aria-label={t('editor.indent')}
              className="flex items-center justify-center rounded p-1.5 text-content-muted transition-surface hover:bg-surface-hover hover:text-content max-sm:min-h-11 max-sm:min-w-11 touch:min-h-11 touch:min-w-11"
            >
              <IndentIncrease className="h-4 w-4" aria-hidden />
            </button>
            <button
              type="button"
              onClick={() => editor.current?.outdent()}
              title={t('editor.outdent')}
              aria-label={t('editor.outdent')}
              className="flex items-center justify-center rounded p-1.5 text-content-muted transition-surface hover:bg-surface-hover hover:text-content max-sm:min-h-11 max-sm:min-w-11 touch:min-h-11 touch:min-w-11"
            >
              <IndentDecrease className="h-4 w-4" aria-hidden />
            </button>
            <span className="hidden sm:inline">{t('kb.editorHint')}</span>
            <span className="flex-1" />
            <Button variant="ghost" size="sm" onClick={() => setDraft(null)}>
              {t('common.cancel')}
            </Button>
            <Button variant="primary" size="sm" onClick={() => save.mutate(draft)}>
              {t('common.save')}
            </Button>
            {/* A phone has no ⌘: name the two ways that exist there, on their own line. */}
            <span className="basis-full sm:hidden">{t('editor.hintTouch')}</span>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={(event) => {
            const target = event.target as HTMLElement;
            // A checklist box — or, on a phone, anywhere on its row — ticks the
            // item and saves the note. It must come before "open the editor",
            // or ticking a box would drop the person into Markdown instead.
            const box =
              target instanceof HTMLInputElement && target.classList.contains('md-task')
                ? target
                : target.closest('a')
                  ? null
                  : (target
                      .closest('li')
                      ?.querySelector<HTMLInputElement>(
                        ':scope > input.md-task, :scope > p > input.md-task',
                      ) ?? null);
            if (box && !box.disabled) {
              event.preventDefault();
              const index = Number(box.dataset.taskIndex ?? '-1');
              if (index >= 0) save.mutate(toggleTaskItem(note.body, index));
              return;
            }
            // Wikilink clicks navigate or create; anything else opens the editor.
            const taskLink = target.closest('a.md-task-link');
            if (taskLink) {
              event.preventDefault();
              const taskId = taskLink.getAttribute('data-task-id');
              if (taskId) openTask(taskId);
              return;
            }
            const anchor = target.closest('a.kb-wikilink');
            if (anchor) {
              event.preventDefault();
              const linked = anchor.getAttribute('data-note-id');
              const phantom = anchor.getAttribute('data-create-title');
              if (linked) navigate(`/kb/notes/${linked}`);
              else if (phantom) {
                createFromPhantom.mutate({ topic_id: note.topic_id, title: phantom });
              }
              return;
            }
            if (target.closest('a')) return; // ordinary links keep working
            // Open the editor on the sentence that was clicked, not at the top.
            setCursor(sourceOffsetFromClick(event, note.body) ?? undefined);
            setDraft(note.body);
          }}
          className={cn(
            'w-full rounded-xl bg-surface px-5 py-4 text-left shadow-xs ring-1',
            'ring-border/60 transition-surface hover:ring-accent/40',
          )}
        >
          <div
            className="markdown text-sm text-content-muted"
            dangerouslySetInnerHTML={{ __html: html }}
          />
          {!note.body && !note.is_sensitive ? (
            <span className="text-sm text-content-subtle">{t('description.add')}</span>
          ) : null}
        </button>
      )}

      {note.backlinks.length > 0 ? (
        <section className="mt-8">
          <h2 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-content-subtle">
            <CornerUpLeft className="h-3.5 w-3.5" aria-hidden />
            {t('kb.backlinks')}
          </h2>
          <ul className="space-y-1">
            {note.backlinks.map((backlink) => (
              <li key={backlink.source_note_id}>
                <Link
                  to={`/kb/notes/${backlink.source_note_id}`}
                  className="text-sm text-accent underline-offset-2 hover:underline"
                >
                  {backlink.source_title}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </Page>
  );
}
